import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom'
import { TreePalm, Clock, Calendar, CheckCircle, XCircle, User, Edit3, Trash2, Plus, CalendarOff } from 'lucide-react'
import { amenityApi, amenityBookApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'
import { showError } from '../utils/showError'
import {
  AMENITY_TYPE_LABELS,
  AMENITY_TYPE_BADGE_VARIANTS,
  AMENITY_TYPES,
} from '../utils/amenityConstants'
import {
  DetailPageLayout,
  Card,
  CardHeader,
  CardBody,
  LoadingSpinner,
  ErrorMessage,
  Badge,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  FormField,
  FormTextarea,
  ConfirmationModal
} from '../components/common'

const AmenityDetail = () => {
  const { amenityId } = useParams()
  const [searchParams] = useSearchParams()
  const buildingId = searchParams.get('buildingId')
  const navigate = useNavigate()
  const [amenity, setAmenity] = useState(null)
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [bookingsLoading, setBookingsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })

  // Unavailable date form
  const [showUdForm, setShowUdForm] = useState(false)
  const [udForm, setUdForm] = useState({ from: '', to: '', reason: '' })
  const [udSubmitting, setUdSubmitting] = useState(false)

  const fetchAmenity = async () => {
    try {
      setLoading(true)
      const response = await amenityApi.get(amenityId)
      const data = response.data?.data || response.data
      setAmenity(data)
    } catch (err) {
      setError(extractErrorMessage(err))
      console.error('Error fetching amenity:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchBookings = async () => {
    try {
      setBookingsLoading(true)
      const response = await amenityBookApi.getByAmenityId(amenityId)
      const data = response.data?.data || response.data
      const items = Array.isArray(data) ? data : []
      setBookings(items)
    } catch (err) {
      console.error('Error fetching bookings:', err)
      setBookings([])
    } finally {
      setBookingsLoading(false)
    }
  }

  useEffect(() => {
    fetchAmenity()
    fetchBookings()
  }, [amenityId])

  const handleConfirm = async (bookingId) => {
    try {
      await amenityBookApi.confirm(bookingId)
      fetchBookings()
    } catch (err) {
      console.error('Error confirming booking:', err)
      showError(err)
    }
  }

  const handleDecline = async (bookingId) => {
    try {
      await amenityBookApi.decline(bookingId)
      fetchBookings()
    } catch (err) {
      console.error('Error declining booking:', err)
      showError(err)
    }
  }

  const handleDeleteBooking = (bookingId) => {
    setConfirmModal({
      open: true,
      title: 'Delete Booking',
      message: 'Are you sure you want to delete this booking? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await amenityBookApi.delete(bookingId)
          fetchBookings()
        } catch (err) {
          console.error('Error deleting booking:', err)
          showError(err)
        }
        setConfirmModal(prev => ({ ...prev, open: false }))
      }
    })
  }

  const handleDeleteAmenity = () => {
    setConfirmModal({
      open: true,
      title: 'Delete Amenity',
      message: 'Are you sure you want to delete this amenity? All associated bookings will also be deleted. This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await amenityApi.delete(amenityId)
          navigate('/amenities')
        } catch (err) {
          console.error('Error deleting amenity:', err)
          showError(err)
        }
        setConfirmModal(prev => ({ ...prev, open: false }))
      }
    })
  }

  const handleAddUnavailableDate = async () => {
    if (!udForm.from || !udForm.to) return
    try {
      setUdSubmitting(true)
      await amenityApi.addUnavailableDate(amenityId, {
        from: new Date(udForm.from).toISOString(),
        to: new Date(udForm.to).toISOString(),
        reason: udForm.reason || null,
      })
      setUdForm({ from: '', to: '', reason: '' })
      setShowUdForm(false)
      fetchAmenity()
    } catch (err) {
      console.error('Error adding unavailable date:', err)
      showError(err)
    } finally {
      setUdSubmitting(false)
    }
  }

  const handleDeleteUnavailableDate = (udId) => {
    setConfirmModal({
      open: true,
      title: 'Delete Unavailable Date',
      message: 'Are you sure you want to remove this unavailable date?',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await amenityApi.deleteUnavailableDate(amenityId, udId)
          fetchAmenity()
        } catch (err) {
          console.error('Error deleting unavailable date:', err)
          showError(err)
        }
        setConfirmModal(prev => ({ ...prev, open: false }))
      }
    })
  }

  const formatTime = (time) => {
    if (!time) return 'N/A'
    return time.substring(0, 5)
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getBookingStatus = (booking) => {
    if (booking.isDeclined) return { label: 'Declined', variant: 'danger' }
    if (booking.isConfirmed) return { label: 'Confirmed', variant: 'success' }
    return { label: 'Pending', variant: 'warning' }
  }

  if (loading) return <LoadingSpinner size="large" />
  if (error) return <ErrorMessage message={error} />

  const amenityType = amenity?.amenityType ?? 0
  const showBookingRules = amenityType === AMENITY_TYPES.BOOKABLE || amenityType === AMENITY_TYPES.TIME_SLOT
  const showTimeSlots = amenityType === AMENITY_TYPES.TIME_SLOT
  const showCapacity = amenityType === AMENITY_TYPES.CAPACITY_LIMITED

  return (
    <DetailPageLayout
      title={amenity?.name || 'Amenity Details'}
      subtitle="View amenity information and bookings"
      backLink={{ label: 'Back to Amenities', href: '/amenities' }}
      actions={[
        <Button
          key="edit"
          as={Link}
          to={`/amenities/${amenityId}/edit?buildingId=${buildingId || amenity?.buildingId}`}
          variant="secondary"
          icon={<Edit3 className="w-4 h-4" />}
        >
          Edit
        </Button>,
        <Button
          key="delete"
          onClick={handleDeleteAmenity}
          variant="danger"
          icon={<Trash2 className="w-4 h-4" />}
        >
          Delete
        </Button>
      ]}
    >
      <div className="space-y-6">
        {/* Amenity Info */}
        <Card>
          <CardHeader
            title="Amenity Information"
            subtitle="Details about this amenity"
          />
          <CardBody>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Name</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {amenity?.name || 'N/A'}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Type</div>
                <div className="col-span-2">
                  <Badge variant={AMENITY_TYPE_BADGE_VARIANTS[amenityType] || 'default'} size="small">
                    {AMENITY_TYPE_LABELS[amenityType] || 'Unknown'}
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Available Hours</div>
                <div className="col-span-2 text-sm text-gray-900 flex items-center">
                  <Clock className="w-4 h-4 mr-1 text-gray-400" />
                  {formatTime(amenity?.startTime)} - {formatTime(amenity?.endTime)}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Roles</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {amenity?.rolesDescription
                    ? <div dangerouslySetInnerHTML={{ __html: amenity.rolesDescription }} />
                    : 'All roles'
                  }
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Total Bookings</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {amenity?.totalBookings || 0}
                </div>
              </div>
              {(amenity?.defaultCoverUrl || amenity?.defaultCoverAttachmentId) && amenity?.defaultCoverUrl && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Cover Image</div>
                  <div className="col-span-2">
                    <img
                      src={amenity.defaultCoverUrl.startsWith('http') ? amenity.defaultCoverUrl : `https://${amenity.defaultCoverUrl}`}
                      alt={amenity.name}
                      className="max-w-xs rounded-lg shadow-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Booking Rules */}
        {showBookingRules && (
          <Card>
            <CardHeader title="Booking Rules" />
            <CardBody>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Requires Confirmation</div>
                  <div className="col-span-2 text-sm text-gray-900">
                    <Badge variant={amenity?.requiresConfirmation ? 'warning' : 'success'} size="small">
                      {amenity?.requiresConfirmation ? 'Yes' : 'No (auto-confirmed)'}
                    </Badge>
                  </div>
                </div>
                {amenity?.maxAdvanceBookingDays != null && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-sm font-medium text-gray-500">Max Advance Booking</div>
                    <div className="col-span-2 text-sm text-gray-900">{amenity.maxAdvanceBookingDays} days</div>
                  </div>
                )}
                {amenity?.minAdvanceBookingHours != null && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-sm font-medium text-gray-500">Min Notice</div>
                    <div className="col-span-2 text-sm text-gray-900">{amenity.minAdvanceBookingHours} hours</div>
                  </div>
                )}
                {amenity?.maxBookingsPerMemberPerMonth != null && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-sm font-medium text-gray-500">Max Bookings / Member / Month</div>
                    <div className="col-span-2 text-sm text-gray-900">{amenity.maxBookingsPerMemberPerMonth}</div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Time Slots */}
        {showTimeSlots && amenity?.timeSlots?.length > 0 && (
          <Card>
            <CardHeader title="Time Slots" subtitle="Predefined booking slots" />
            <CardBody>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell header>Name</TableCell>
                    <TableCell header>Start</TableCell>
                    <TableCell header>End</TableCell>
                    <TableCell header>Order</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {amenity.timeSlots.map((slot) => (
                    <TableRow key={slot.amenityTimeSlotId}>
                      <TableCell>
                        <div className="text-sm font-medium text-gray-900">{slot.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">{formatTime(slot.startTime)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">{formatTime(slot.endTime)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">{slot.displayOrder}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardBody>
          </Card>
        )}

        {/* Capacity Info */}
        {showCapacity && (
          <Card>
            <CardHeader title="Capacity Settings" />
            <CardBody>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-sm font-medium text-gray-500">Max Capacity</div>
                  <div className="col-span-2 text-sm text-gray-900">{amenity?.maxCapacity ?? 'N/A'}</div>
                </div>
                {amenity?.checkInExpiryHours != null && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-sm font-medium text-gray-500">Check-in Expiry</div>
                    <div className="col-span-2 text-sm text-gray-900">{amenity.checkInExpiryHours} hours</div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Unavailable Dates */}
        <Card>
          <CardHeader
            title="Unavailable Dates"
            subtitle="Dates when this amenity is not available"
            action={
              <Button
                onClick={() => setShowUdForm(!showUdForm)}
                variant="secondary"
                size="small"
                icon={<Plus className="w-3 h-3" />}
              >
                Add Date
              </Button>
            }
          />
          <CardBody>
            {showUdForm && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="From"
                    type="datetime-local"
                    value={udForm.from}
                    onChange={(e) => setUdForm(prev => ({ ...prev, from: e.target.value }))}
                    required
                  />
                  <FormField
                    label="To"
                    type="datetime-local"
                    value={udForm.to}
                    onChange={(e) => setUdForm(prev => ({ ...prev, to: e.target.value }))}
                    required
                  />
                </div>
                <FormTextarea
                  label="Reason (optional)"
                  value={udForm.reason}
                  onChange={(e) => setUdForm(prev => ({ ...prev, reason: e.target.value }))}
                  rows={2}
                  placeholder="e.g., Maintenance, Renovation..."
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddUnavailableDate}
                    variant="primary"
                    size="small"
                    loading={udSubmitting}
                    disabled={!udForm.from || !udForm.to}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => { setShowUdForm(false); setUdForm({ from: '', to: '', reason: '' }) }}
                    variant="ghost"
                    size="small"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {(!amenity?.unavailableDates || amenity.unavailableDates.length === 0) ? (
              <EmptyState
                icon={CalendarOff}
                title="No unavailable dates"
                description="This amenity has no scheduled unavailable dates."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell header>From</TableCell>
                    <TableCell header>To</TableCell>
                    <TableCell header>Reason</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {amenity.unavailableDates.map((ud) => (
                    <TableRow key={ud.amenityUnavailableDateId}>
                      <TableCell>
                        <div className="text-sm">{formatDateTime(ud.from)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{formatDateTime(ud.to)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">{ud.reason || '-'}</div>
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleDeleteUnavailableDate(ud.amenityUnavailableDateId)}
                          variant="ghost"
                          size="small"
                          icon={<Trash2 className="w-3 h-3" />}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardBody>
        </Card>

        {/* Bookings */}
        <Card>
          <CardHeader
            title="Bookings"
            subtitle="Reservations for this amenity"
          />
          <CardBody>
            {bookingsLoading ? (
              <LoadingSpinner />
            ) : bookings.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title="No bookings"
                description="No reservations found for this amenity."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell header>Member</TableCell>
                    <TableCell header>Reservation Date</TableCell>
                    <TableCell header>Time Slot</TableCell>
                    <TableCell header>Status</TableCell>
                    <TableCell header>Message</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => {
                    const status = getBookingStatus(booking)
                    return (
                      <TableRow key={booking.amenityBookId}>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <User className="w-4 h-4 mr-1 text-gray-400" />
                            {booking.memberName || `Member #${booking.buildingMemberId}`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                            {formatDate(booking.reservationDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-600">
                            {formatDateTime(booking.from)} - {formatDateTime(booking.to)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant} size="small">
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-600 max-w-xs truncate">
                            {booking.message || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {!booking.isConfirmed && !booking.isDeclined && (
                              <>
                                <Button
                                  onClick={() => handleConfirm(booking.amenityBookId)}
                                  variant="accent"
                                  size="small"
                                  icon={<CheckCircle className="w-3 h-3" />}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  onClick={() => handleDecline(booking.amenityBookId)}
                                  variant="danger"
                                  size="small"
                                  icon={<XCircle className="w-3 h-3" />}
                                >
                                  Decline
                                </Button>
                              </>
                            )}
                            <Button
                              onClick={() => handleDeleteBooking(booking.amenityBookId)}
                              variant="ghost"
                              size="small"
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmVariant={confirmModal.variant}
        confirmText="Delete"
      />
    </DetailPageLayout>
  )
}

export default AmenityDetail
